import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../hooks/useSession';
import Header from '../components/ui/Header';
import Card from '../components/ui/Card';
import FileUpload from '../components/ui/FileUpload';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import API_ENDPOINTS from '../config/api';

interface QuoteData {
  id: string;
  file_name: string;
  created_at: string;
  total_price: number;
}

interface UploadError {
  title: string;
  message: string;
  suggestions: string[];
}

interface FeaturedProduct {
  name: string;
  description: string;
  url: string;
}

const FEATURED_PRODUCTS: FeaturedProduct[] = [
  {
    name: '72mm Steel Flywheel (Thin)',
    description: 'A lighter-weight version of our 72mm steel flywheel, ready to ship.',
    url: 'https://exactoline.com/products/72mm-steel-flywheel-thin?variant=47688693383389',
  },
  {
    name: '72mm Steel Flywheel',
    description: 'Our standard 72mm steel flywheel, machined and ready to ship.',
    url: 'https://exactoline.com/products/72mm-steel-flywheel?variant=47240018624733',
  },
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useSession();
  const [isUploading, setIsUploading] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [quotes, setQuotes] = useState<QuoteData[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [visitCount, setVisitCount] = useState<number | null>(null);
  const visitRecorded = useRef(false);

  // Record a site visit once per browser session and display the running total.
  useEffect(() => {
    if (visitRecorded.current) return;
    visitRecorded.current = true;

    const alreadyCounted = sessionStorage.getItem('swiftfab_visit_counted') === 'true';
    const endpoint = alreadyCounted ? API_ENDPOINTS.GET_VISITS : API_ENDPOINTS.RECORD_VISIT;

    const trackVisit = async () => {
      try {
        const response = await fetch(endpoint, {
          method: alreadyCounted ? 'GET' : 'POST',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && typeof data.count === 'number') {
            setVisitCount(data.count);
            if (!alreadyCounted) {
              sessionStorage.setItem('swiftfab_visit_counted', 'true');
            }
          }
        }
      } catch (error) {
        console.error('Visit tracking error:', error);
      }
    };

    trackVisit();
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setIsUploading(true);
    setUploadError(null); // Clear any previous errors
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);

      const response = await fetch(API_ENDPOINTS.CREATE_QUOTE, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          navigate(`/quote/${result.quote_id}`);
        } else {
          setUploadError({
            title: 'Upload Failed',
            message: result.message || 'Upload failed',
            suggestions: []
          });
        }
      } else {
        // Try to get error details from response and provide user-friendly messages
        let errorTitle = 'Upload Failed';
        let errorMessage = 'An unexpected error occurred during upload.';
        let errorSuggestions: string[] = [];

        try {
          const errorData = await response.json();

          // First check for part validation errors in any response (they might come as 500 errors)
          if (errorData.detail && (
              errorData.detail.includes("No supported parts found") ||
              errorData.detail.includes("Failed to parse STEP file") ||
              errorData.detail.includes("flat sheet metal parts") ||
              errorData.detail.includes("sheet/flat")
            )) {
            errorTitle = 'Unsupported Part Types';
            errorMessage = 'Your part needs to be a flat plate. Only flat sheet metal plates are accepted for quoting.';
            errorSuggestions = [
              'Your part must be a flat, 2D plate shape (not bent, formed, or 3D)',
              'Remove any bends, curves, or thickness variations from your design',
              'If your design has multiple parts, upload them as separate flat plates',
              'Tubes, blocks, castings, and complex machined parts are not supported',
              'Use only simple flat geometries for laser cutting quotes'
            ];
          } else if (response.status === 400) {
            if (errorData.detail === "Only STEP/STP files are supported") {
              errorTitle = 'Unsupported File Type';
              errorMessage = 'Only STEP (.step) and STP (.stp) files are supported.';
              errorSuggestions = [
                'Make sure your file has a .step or .stp extension',
                'Convert your CAD file to STEP format if needed'
              ];
            } else {
              errorTitle = 'Invalid File';
              errorMessage = errorData.detail || 'The uploaded file is not valid.';
            }
          } else if (response.status === 413) {
            errorTitle = 'File Too Large';
            errorMessage = 'The uploaded file exceeds the maximum allowed size.';
            errorSuggestions = [
              'Reduce file size by simplifying geometry',
              'Split large assemblies into smaller parts',
              'Compress the file if possible'
            ];
          } else if (response.status >= 500) {
            errorTitle = 'Server Error';
            errorMessage = 'A server error occurred while processing your file.';
            errorSuggestions = [
              'Try uploading again in a few minutes',
              'Contact support if the problem persists'
            ];
          } else {
            // Handle other error formats
            if (errorData.detail) {
              if (typeof errorData.detail === 'string') {
                errorMessage = errorData.detail;
              } else if (Array.isArray(errorData.detail)) {
                errorMessage = errorData.detail.map((err: unknown) =>
                  typeof err === 'string' ? err : JSON.stringify(err)
                ).join('\n');
              } else {
                errorMessage = JSON.stringify(errorData.detail);
              }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch {
          // If we can't parse the error response, provide status-based error
          if (response.status === 413) {
            errorTitle = 'File Too Large';
            errorMessage = 'The uploaded file is too large to process.';
            errorSuggestions = ['Try a smaller file or contact support'];
          } else {
            errorTitle = 'Connection Error';
            errorMessage = `Upload failed with server error (${response.status}).`;
            errorSuggestions = ['Check your internet connection and try again'];
          }
        }

        // Set the enhanced error information
        setUploadError({
          title: errorTitle,
          message: errorMessage,
          suggestions: errorSuggestions
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError({
        title: 'Network Error',
        message: 'Network error occurred during upload.',
        suggestions: ['Check your internet connection and try again']
      });
    } finally {
      setIsUploading(false);
    }
  };

  const loadAllQuotes = async () => {
    setLoadingQuotes(true);
    try {
      const response = await fetch(API_ENDPOINTS.GET_QUOTES, {
        headers: {
          'X-Session-ID': sessionId
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setQuotes(data.quotes);
        }
      }
    } catch (error) {
      console.error('Load quotes error:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const handleViewQuotes = () => {
    setShowQuotes(!showQuotes);
    if (!showQuotes) {
      loadAllQuotes();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      <Header onViewQuotes={handleViewQuotes} showQuotes={showQuotes} />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        <Card
          title="Upload STEP File"
          icon={
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          }
        >
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm sm:text-base font-semibold text-gray-700 mb-3 sm:mb-4">
                Select STEP File (.step or .stp)
              </label>
              <FileUpload
                onFileSelect={handleFileUpload}
                isUploading={isUploading}
                accept=".step,.stp"
                maxSize={25}
              />
            </div>

            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      {uploadError.title}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{uploadError.message}</p>
                      {uploadError.suggestions && uploadError.suggestions.length > 0 && (
                        <div className="mt-3">
                          <p className="font-medium text-red-800 mb-2">Suggestions:</p>
                          <ul className="list-disc list-inside space-y-1 text-xs">
                            {uploadError.suggestions.map((suggestion, index) => (
                              <li key={index}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setUploadError(null)}
                        className="text-sm font-medium text-red-800 hover:text-red-600"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card
          title="Featured Products"
          icon={
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {FEATURED_PRODUCTS.map((product) => (
              <div
                key={product.url}
                className="bg-white/30 rounded-xl p-4 sm:p-6 border border-gray-200 flex flex-col justify-between"
              >
                <div>
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg mb-1">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm mb-4">
                    {product.description}
                  </p>
                </div>
                <Button
                  onClick={() => window.open(product.url, '_blank')}
                  size="sm"
                  className="w-full"
                >
                  View Product
                </Button>
              </div>
            ))}
          </div>
        </Card>

        {showQuotes && (
          <Card
            title="My Quotes"
            icon={
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          >
            {loadingQuotes ? (
              <LoadingSpinner size="lg" text="Loading quotes..." />
            ) : quotes.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-gray-600 text-base sm:text-lg px-4">No quotes found. Upload a STEP file to create your first quote.</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-3 sm:-mx-4 md:-mx-6 lg:mx-0">
                <div className="min-w-full px-3 sm:px-4 md:px-6 lg:px-0">
                  {/* Mobile Card Layout */}
                  <div className="block sm:hidden space-y-3">
                    {quotes.map((quote: QuoteData, index: number) => (
                      <div key={quote.id} className="bg-white/30 rounded-lg p-4 border border-gray-200">
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <div className="font-medium text-gray-900 text-sm truncate flex-1 mr-2">
                              {quote.id}
                            </div>
                            <div className="text-sm font-bold text-green-600">
                              ${(quote.total_price || 0).toFixed(2)}
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            {quote.file_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </div>
                          <div className="pt-2">
                            <Button
                              onClick={() => navigate(`/quote/${quote.id}`)}
                              size="sm"
                              className="w-full"
                            >
                              View Quote
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table Layout */}
                  <table className="hidden sm:table w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm">Quote ID</th>
                        <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm">File Name</th>
                        <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm hidden md:table-cell">Created</th>
                        <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm">Total Price</th>
                        <th className="text-left py-3 sm:py-4 px-2 sm:px-4 font-semibold text-gray-700 text-xs sm:text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((quote: QuoteData, index: number) => (
                        <tr key={quote.id} className={`border-b border-gray-100 hover:bg-gray-50/50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white/30' : 'bg-white/10'}`}>
                          <td className="py-3 sm:py-4 px-2 sm:px-4">
                            <div className="font-medium text-gray-900 text-xs sm:text-sm truncate max-w-24 sm:max-w-none">
                              {quote.id}
                            </div>
                          </td>
                          <td className="py-3 sm:py-4 px-2 sm:px-4 text-xs sm:text-sm text-gray-600 truncate max-w-32 sm:max-w-none">
                            {quote.file_name}
                          </td>
                          <td className="py-3 sm:py-4 px-2 sm:px-4 text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 sm:py-4 px-2 sm:px-4 text-xs sm:text-sm font-bold text-green-600">
                            ${(quote.total_price || 0).toFixed(2)}
                          </td>
                          <td className="py-3 sm:py-4 px-2 sm:px-4">
                            <Button
                              onClick={() => navigate(`/quote/${quote.id}`)}
                              size="sm"
                            >
                              View Quote
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        )}

        <footer className="pt-2 pb-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/40 border border-gray-200 text-gray-600 text-xs sm:text-sm shadow-sm">
            <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="font-medium">
              {visitCount === null ? 'Loading visits…' : `${visitCount.toLocaleString()} visits`}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Home;
